// Per-module rather than the `crypto-js` barrel: the barrel pulls every cipher
// and digest (AES, DES, PBKDF2, the whole SHA family) for one MD5 call.
import CryptoJS from 'crypto-js/core';
import 'crypto-js/md5';
import 'crypto-js/enc-base64';
import 'crypto-js/lib-typedarrays';
import { http as v1Https } from '@/lib/http';

/**
 * Generate MD5 hash for file integrity check
 */
export const md5File = async (file: File): Promise<string> => {
  const wordArray = CryptoJS.lib.WordArray.create(await file.arrayBuffer());
  return CryptoJS.MD5(wordArray).toString(CryptoJS.enc.Base64);
};

/**
 * Upload file directly to storage and return signed ID
 * @param file - File to upload
 * @param _fieldName - accepted for call-site compatibility; not used
 * @returns signed_id string
 */
export const handleDirectFileUpload = async (
  file: File,
  _fieldName?: string
): Promise<string> => {
  const { name, size, type } = file;

  // Create payload for direct upload
  const payload = {
    blob: {
      filename: name,
      content_type: type,
      byte_size: size,
      checksum: await md5File(file),
    },
  };

  try {
    // Step 1: Get direct upload URL and signed ID
    // @ts-expect-error the http helper returns `unknown`; the direct-upload
    // response shape is not modelled yet
    const { direct_upload, signed_id } = await v1Https.post(
      '/direct_uploads',
      payload
    );

    // Check if the file is an instance of File
    if (!(file instanceof File)) {
      throw new TypeError('Provided input is not a valid File object');
    }

    // Step 2: Upload file to storage
    const uploadHeaders = new Headers(direct_upload.headers);
    const uploadResponse = await fetch(direct_upload.url, {
      method: 'PUT',
      headers: uploadHeaders,
      body: file,
    });

    if (!uploadResponse.ok) {
      throw new Error(`Failed to upload file: ${uploadResponse.statusText}`);
    }

    return signed_id;
  } catch (error) {
    console.error('Error in handleDirectFileUpload:', error);
    throw error;
  }
};

/**
 * Process form values to upload all files and return signed IDs
 * @param values - Form values containing File objects
 * @returns Same values structure with Files replaced by signed_id strings
 */
export const processFormFilesForUpload = async <T extends Record<string, any>>(
  values: T
): Promise<T> => {
  const processedValues: Record<string, any> = { ...values };

  for (const key of Object.keys(processedValues)) {
    const value = processedValues[key];

    // Single file
    if (value instanceof File) {
      processedValues[key] = await handleDirectFileUpload(value, key);
    }
    // File array (for multi-upload fields)
    else if (Array.isArray(value) && value[0] instanceof File) {
      if (key.startsWith('custom_')) {
        // Custom fields: only first file
        processedValues[key] = await handleDirectFileUpload(value[0], key);
      } else {
        // Static fields: all files
        processedValues[key] = await Promise.all(
          value.map((file) => handleDirectFileUpload(file, key))
        );
      }
    }
  }

  return processedValues as T;
};
